import {
  // Auth Slice
  authenticate,
  getEmailOtp,
  getIsAuthenticated,
  getOtp,
  getUserData,

  // Auth API
  useResendOTPMutation,
  useSendOTPMutation,
  useVerifyOTPMutation,
  useLazyGetUserPermissionsQuery,
  useGetMappedWorkspacesForSupportUserMutation,

  // Store
  dispatch,
  useAppSelector,

  // Onboarding
  SignUpStepEnum,

  // Config
  useCreateConfigSettingsMutation,
  useLazyGetConfigDetailsQuery,

  // Workspace
  useLazyGetWorkspaceQuery,
  getWorkspaceId,
  JTBDEventType,
  onAuthSuccess,
  useEventTracking,
  AuthChannel,
} from '@zonofi/seller-rtk-store';
import { SellerDetails } from '@zonofi/zo-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getConfigValue } from '@zonofi/common/functions/get-config-value/get-config-value';
import _ from 'lodash';
import { AGENT_SETUP_ROUTE } from '../navigation';
import { CountryObject } from '@zonofi/i18n';
import { isPhoneNumberValid } from '@zonofi/common/functions/format-phone';

export const useSignUpNow = ({
  onOtpVerified,
  onUpdateValue,
  sellerDetails,
}: {
  onOtpVerified: () => void;
  onUpdateValue: (key: string, value: string | boolean) => void;
  sellerDetails: SellerDetails;
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const step = searchParams.get('step');
  const encodedAuthData = searchParams.get('data');

  const otpFromStore = useAppSelector(getOtp);
  const emailOtpFromStore = useAppSelector(getEmailOtp);
  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const location = useLocation();
  const [isLoginScreen, setIsLoginScreen] = useState<boolean>(
    location.pathname === '/login'
  );
  const [getOrderConfigDetails] = useLazyGetConfigDetailsQuery();
  const [getUserPermissions, getUserPermissionsResponse] =
    useLazyGetUserPermissionsQuery();
  // const loggedInUserType = useGetLoggedInUserType();

  const mobileOtpFromStore = useAppSelector(getOtp);

  const [loginVal, setLoginVal] = useState<string>();
  const [otpToLogin, setOtpToLogin] = useState<string>('');
  const [showOtpInput, setShowOtpInput] = useState<boolean>(false);
  const [inputErrors, setInputErrors] = useState<{
    isError: boolean;
    errorMsg: string;
  }>({
    isError: false,
    errorMsg: '',
  });
  const [selectedCountry, setSelectedCountry] = useState<CountryObject>({
    name: 'India',
    flag: '🇮🇳',
    code: 'IN',
    dial_code: '+91',
    currency_code: 'INR',
    time_zones: ['Asia/Kolkata'],
  });
  const [countryDrawerOpen, setCountryDrawerOpen] = useState<boolean>(false);
  const mobileInputRef = useRef(null);

  const [sendOtpAPI, sendOtpAPIResponse] = useSendOTPMutation();
  const [verifyOtpAPI, verifyOtpAPIResponse] = useVerifyOTPMutation();
  const [resendOtp, resendOTPResponse] = useResendOTPMutation();
  const [createConfigSettings] = useCreateConfigSettingsMutation();
  const [getMappedWorkspacesForSupportUser] =
    useGetMappedWorkspacesForSupportUserMutation();
  const [fetchWorkspaces, workspacesResponse] = useLazyGetWorkspaceQuery();

  const userData = useAppSelector(getUserData);
  const workspaceId = useAppSelector(getWorkspaceId);

  const [timer, setTimer] = useState<number>(120);
  const [resendEnabled, setResendEnabled] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(
      remainingSeconds
    ).padStart(2, '0')}`;
  };

  const handleInputChange = (e: { target: { value: string } }) => {
    const inputValue = e.target.value;

    // Only allow email on login screen
    if (sellerDetails.isEmail && isLoginScreen) {
      setLoginVal(inputValue);
    } else {
      // For non-login screens, only allow numeric input
      if (inputValue.length > 0 && !/^\d+$/.test(inputValue)) {
        setInputErrors({
          isError: true,
          errorMsg: 'Only numbers allowed',
        });
        return;
      }

      const value = inputValue.replace(/\D/g, '');
      setLoginVal(value);

      // Clear or validate error message
      if (value.length === 0) {
        setInputErrors({ isError: false, errorMsg: '' });
      } else if (inputErrors?.isError) {
        validateInput(value);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Only restrict input on non-login screens
    if (!isLoginScreen) {
      // Allow navigation keys, backspace, delete, etc.
      if (
        e.key === 'Backspace' ||
        e.key === 'Delete' ||
        e.key === 'Tab' ||
        e.key === 'Enter' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown'
      ) {
        return;
      }

      // Only allow numeric input
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
        setInputErrors({
          isError: true,
          errorMsg: 'Only numbers allowed',
        });
      }
    }
  };
  const validateInput = (val: string): boolean => {
    const isValidEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };

    // Only allow email validation on login screen
    if (sellerDetails.isEmail && isLoginScreen) {
      const isValid = isValidEmail(val);
      setInputErrors({
        isError: !isValid,
        errorMsg: isValid ? '' : 'Invalid Email Id',
      });
      return isValid;
    } else {
      // For non-login screens, only allow mobile numbers
      const isNumeric = /^\d+$/.test(val);
      const isValid =
        isNumeric && isPhoneNumberValid(val, selectedCountry?.code);

      setInputErrors({
        isError: !isValid,
        errorMsg: !isNumeric
          ? 'Only numbers allowed'
          : !isValid
            ? 'Invalid Mobile Number'
            : '',
      });
      return isValid;
    }
  };

  const onGenerateOtp = useCallback(() => {
    const isValid = validateInput(loginVal || '');
    let payload = {};

    // Only allow email on login screen
    if (sellerDetails.isEmail && isLoginScreen) {
      payload = {
        authChannel: 'email' as AuthChannel,
        email: loginVal,
        doCreateUser: !isLoginScreen ? true : false,
      };
    } else {
      // For non-login screens, always use mobile
      payload = {
        authChannel: 'mobile' as AuthChannel,
        mobile: `${selectedCountry?.dial_code}${loginVal}`,
        doCreateUser: !isLoginScreen ? true : false,
      };
    }

    if (isValid) {
      // finService.trackEvent( isLoginScreen ? 'login-get-otp' : 'signup-get-otp', {
      //   user_id: userData?.id,
      //   email: userData?.email,
      //   name: userData?.firstName + ' ' + userData?.lastName,
      //   sellerWorkspaceId: workspaceId,
      // });
      sendOtpAPI({ ...payload });
    }
  }, [loginVal, sellerDetails.isEmail, isLoginScreen, selectedCountry]);

  const onVerifyOtp = useCallback(() => {
    // Only allow email on login screen
    if (sellerDetails.isEmail && isLoginScreen) {
      verifyOtpAPI({
        authChannel: 'email' as AuthChannel,
        email: loginVal,
        otp: otpToLogin,
        mfaStatus: false,
      });
    } else {
      // For non-login screens, always use mobile
      verifyOtpAPI({
        authChannel: 'mobile' as AuthChannel,
        mobile: `${selectedCountry?.dial_code}${loginVal}`,
        otp: otpToLogin,
        mfaStatus: false,
      });
    }
    // finService.trackEvent( isLoginScreen ? 'login-verify-otp' : 'signup-verify-otp', {
    //   user_id: userData?.id,
    //   email: userData?.email,
    //   name: userData?.firstName + ' ' + userData?.lastName,
    //   sellerWorkspaceId: workspaceId,
    // });
  }, [loginVal, otpToLogin, sellerDetails.isEmail, isLoginScreen]);

  const resendOTPInput = () => {
    // Only allow email on login screen
    if (sellerDetails.isEmail && isLoginScreen) {
      resendOtp({ authChannel: 'email' as AuthChannel, email: loginVal });
    } else {
      // For non-login screens, always use mobile
      resendOtp({
        authChannel: 'mobile' as AuthChannel,
        mobile: `${selectedCountry?.dial_code}${loginVal}`,
      });
    }
    setOtpToLogin('');
    setErrorMessage('');
    setTimer(120);
  };

  const openCountriesDrawer = () => {
    setCountryDrawerOpen(true);
  };

  const closeCountriesDrawer = () => {
    setCountryDrawerOpen(false);
  };

  useEffect(() => {
    // Only allow email detection on login screen
    if (isLoginScreen) {
      onUpdateValue('isEmail', isNaN(Number(loginVal?.[0])));
    } else {
      // For non-login screens, always set isEmail to false
      onUpdateValue('isEmail', false);
    }
  }, [loginVal, isLoginScreen]);

  useEffect(() => {
    if (step === SignUpStepEnum.OTP) {
      setShowOtpInput(true);
    } else {
      setShowOtpInput(false);
    }
  }, [step]);

  useEffect(() => {
    if (sendOtpAPIResponse.isSuccess) {
      // Only allow email on login screen
      if (sellerDetails.isEmail && isLoginScreen) {
        if (sendOtpAPIResponse?.data?.email?.resendAttemptsLeft > 0) {
          navigate('/verifyOtp');
          setShowOtpInput(true);
          setTimer(120);
          setResendEnabled(false);
        } else if (sendOtpAPIResponse?.data?.email?.message) {
          setErrorMessage(sendOtpAPIResponse?.data?.email?.message);
        } else if (sendOtpAPIResponse?.data?.message) {
          setErrorMessage(sendOtpAPIResponse?.data?.message);
        }
      } else {
        // For non-login screens, always use mobile
        if (sendOtpAPIResponse?.data?.mobile?.resendAttemptsLeft > 0) {
          navigate('/verifyOtp');
          setShowOtpInput(true);
          setTimer(120);
          setResendEnabled(false);
        } else if (sendOtpAPIResponse?.data?.mobile?.message) {
          setErrorMessage(sendOtpAPIResponse?.data?.mobile?.message);
        } else if (sendOtpAPIResponse?.data?.message) {
          setInputErrors({
            isError: true,
            errorMsg: sendOtpAPIResponse?.data?.message,
          });
        }
      }
    } else if (sendOtpAPIResponse.isError) {
      console.log('sendOtpAPIResponse: ', sendOtpAPIResponse);
      //@ts-ignore
      sendOtpAPIResponse?.error?.data?.message &&
        setInputErrors({
          isError: true,
          //@ts-ignore
          errorMsg: sendOtpAPIResponse?.error?.data?.message,
        });
    }
  }, [sendOtpAPIResponse, isLoginScreen]);

  const { trackEvent } = useEventTracking();

  useEffect(() => {
    if (encodedAuthData) {
      console.log('encodedAuthData: ', encodedAuthData);
      const decodedData = JSON.parse(atob(encodedAuthData));
      console.log('decodedData: ', decodedData);

      dispatch(onAuthSuccess(decodedData));
      dispatch(authenticate());
      navigate(AGENT_SETUP_ROUTE.path);
    } else if (verifyOtpAPIResponse?.isSuccess) {
      if (verifyOtpAPIResponse?.data?.alertMessage) {
        setErrorMessage(verifyOtpAPIResponse?.data?.alertMessage || '');
      } else if (!isLoginScreen && !verifyOtpAPIResponse?.data?.workspaceId) {
        // Only allow email on login screen
        if (sellerDetails.isEmail && isLoginScreen) {
          onUpdateValue('emailId', loginVal || '');
        } else {
          // For non-login screens, always use mobile
          onUpdateValue('dial_code', selectedCountry?.dial_code);
          onUpdateValue('phoneNumber', `${loginVal}` || '');
        }
        const encodedData = btoa(JSON.stringify(verifyOtpAPIResponse?.data));
        localStorage.removeItem('encodedData');
        localStorage.setItem('encodedData', encodedData);
        localStorage.setItem('authData', encodedData);
        trackEvent(JTBDEventType.SIGNUP_VERIFY_OTP, {
          user_id: userData?.id,
          email: userData?.email,
          name: userData?.firstName + ' ' + userData?.lastName,
          sellerWorkspaceId: workspaceId,
          ...sellerDetails,
        });
        onOtpVerified();
      } else if (!isLoginScreen && verifyOtpAPIResponse?.data?.workspaceId) {
        const encodedData = btoa(JSON.stringify(verifyOtpAPIResponse?.data));
        localStorage.removeItem('encodedData');
        localStorage.setItem('encodedData', encodedData);
        localStorage.setItem('authData', encodedData);
        trackEvent(JTBDEventType.SIGNUP_VERIFY_OTP, {
          user_id: userData?.id,
          email: userData?.email,
          name: userData?.firstName + ' ' + userData?.lastName,
          sellerWorkspaceId: workspaceId,
          ...sellerDetails,
        });
        fetchWorkspaces();
        // onOtpVerified();
      } else if (isLoginScreen) {
        // Encrypt sensitive data before redirect
        const dataToTransfer = verifyOtpAPIResponse?.data;
        dispatch(onAuthSuccess(dataToTransfer));

        // Simple base64 encoding (use proper encryption in production)
        const encodedData = btoa(JSON.stringify(dataToTransfer));

        // Store in localStorage for access in different flows
        localStorage.setItem('authData', encodedData);
        let baseUrl = getConfigValue('APP_ENDPOINT');
        if (baseUrl.endsWith('/')) {
          baseUrl = baseUrl.slice(0, -1);
        }
        console.log('baseUrl: ', baseUrl);
        // window.location.href = `${baseUrl}/authenticate?data=${encodedData}`;
        fetchWorkspaces();
      }
    }
  }, [verifyOtpAPIResponse, encodedAuthData]);

  // const getInvites = () => {
  //   fetchTeamInvites()
  //     .unwrap()
  //     .then((response) => {
  //       if (response && !_.isEmpty(response)) {
  //         navigateToTeamInviteAccept &&
  //           navigateToTeamInviteAccept(response?.[0]);
  //       } else {
  //         getPrincipalInvites();
  //       }
  //     })
  //     .catch(() => {
  //       getPrincipalInvites();
  //     });
  // };

  useEffect(() => {
    if (workspacesResponse.isSuccess && workspacesResponse.data) {
      if (!_.isEmpty(workspacesResponse.data)) {
        dispatch(authenticate());
        navigate(AGENT_SETUP_ROUTE.path);
        // if (
        //   fromURL &&
        //   fromURL !== '/' &&
        //   verifyUserResponse.data &&
        //   !['support', 'admin'].includes(verifyUserResponse.data.role)
        // ) {
        //   console.log('here');
        //   navigateFunction(fromURL);
        // } else {
        //   console.log('number: ', value);
        //   if (TourEnabledLogins.includes(value)) {
        //     console.log('run tour');
        //     navigateFunction(ALL_ROUTES.HOME_PAGE.path, {
        //       state: { runTour: true },
        //     });
        //   } else {
        //     navigateFunction(ALL_ROUTES.HOME_PAGE.path);
        //   }
        // }
      } else {
        onOtpVerified();
        // getInvites();
      }
    }
  }, [workspacesResponse]);

  useEffect(() => {
    // Only allow email on login screen
    if (sellerDetails.isEmail && isLoginScreen) {
      setOtpToLogin(emailOtpFromStore);
    } else {
      // For non-login screens, always use mobile
      setOtpToLogin(otpFromStore);
    }
  }, [otpFromStore, emailOtpFromStore, isLoginScreen]);
  useEffect(() => {
    if (showOtpInput) {
      setErrorMessage('');
    }
  }, [showOtpInput]);
  useEffect(() => {
    if (!showOtpInput) {
      return () => {};
    }
    let interval: NodeJS.Timeout | null = null;
    if (timer > 0 && !resendEnabled) {
      //@ts-ignore
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setResendEnabled(true);
      setOtpToLogin('');
      if (interval) {
        clearInterval(interval);
      }
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer, resendEnabled, showOtpInput]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (showOtpInput) {
          onVerifyOtp();
        } else {
          onGenerateOtp();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onGenerateOtp, onVerifyOtp, showOtpInput]);
  useEffect(() => {
    if (location.pathname === '/login') {
      setIsLoginScreen(true);
    }
  }, [location]);

  useEffect(() => {
    if (resendOTPResponse.isSuccess && resendOTPResponse.data) {
      if (resendOTPResponse?.data?.sendOtp) {
        setOtpToLogin(resendOTPResponse?.data?.otp?.toString());
        setResendEnabled(false);
      } else {
        const resendAttemptsLeft = resendOTPResponse?.data?.resendAttemptsLeft;
        const message = resendOTPResponse?.data?.message;
        const timeLeft = resendOTPResponse?.data?.timeLeft;
        const uom = resendOTPResponse?.data?.uom;
        if (message) {
          if (resendAttemptsLeft === 0 && timeLeft) {
            setErrorMessage(message);
            setOtpToLogin('');
            setResendEnabled(false);
            setTimer(timeLeft);
            // setTimeLeft({ time: timeLeft, uom: uom });
          }
        }
      }
    } else if (resendOTPResponse.isError) {
      console.log('resendOTPResponse: ', resendOTPResponse);
      //@ts-ignore
      if (resendOTPResponse?.error?.status === 404) {
        // Only allow email on login screen
        if (sellerDetails.isEmail && isLoginScreen) {
          sendOtpAPI({ authChannel: 'email' as AuthChannel, email: loginVal });
        } else {
          // For non-login screens, always use mobile
          sendOtpAPI({
            authChannel: 'mobile' as AuthChannel,
            mobile: `${selectedCountry?.dial_code}${loginVal}`,
          });
        }
      } else {
        //@ts-ignore
        setErrorMessage(resendOTPResponse?.error?.data?.message);
      }
    }
    // handleOTPResponse(resendOTPResponse);
  }, [resendOTPResponse]);

  useEffect(() => {
    if (sellerDetails.phoneNumber || (sellerDetails.emailId && isLoginScreen)) {
      setShowOtpInput(true);
    }
  }, [
    sellerDetails.isEmail,
    sellerDetails.phoneNumber,
    sellerDetails.emailId,
    isLoginScreen,
  ]);

  useEffect(() => {
    if (sellerDetails.phoneNumber) {
      setShowOtpInput(true);
      setLoginVal(sellerDetails.phoneNumber.replace('091', ''));
      onUpdateValue?.('isEmail', false);
    } else if (sellerDetails?.emailId && isLoginScreen) {
      // Only allow email on login screen
      setShowOtpInput(true);
      setLoginVal(sellerDetails.emailId);
      onUpdateValue?.('isEmail', true);
    }
  }, [isLoginScreen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const anchorEl = mobileInputRef.current;
      const drawerEl = document.querySelector('.countries-list'); // Adjust selector if needed

      if (
        countryDrawerOpen &&
        anchorEl &&
        !anchorEl.contains(event.target as Node) &&
        drawerEl &&
        !drawerEl.contains(event.target as Node)
      ) {
        closeCountriesDrawer();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [countryDrawerOpen]);

  useEffect(() => {
    const country = localStorage.getItem('selectedCountry');
    if (country) {
      const countryObject = JSON.parse(country);
      setSelectedCountry(countryObject);
    }
  }, []);

  return {
    loginVal,
    setLoginVal,
    otpToLogin,
    setOtpToLogin,
    isEmail: sellerDetails.isEmail,
    onGenerateOtp,
    sendOtpAPIResponse,
    showOtpInput,
    setShowOtpInput,
    onVerifyOtp,
    verifyOtpAPIResponse,
    timer,
    formatTime,
    resendEnabled,
    errorMessage,
    resendOTPInput,
    setErrorMessage,
    inputErrors,
    validateInput,
    setInputErrors,
    isLoginScreen,
    setIsLoginScreen,
    handleInputChange,
    handleKeyDown,
    mobileInputRef,
    countryDrawerOpen,
    openCountriesDrawer,
    closeCountriesDrawer,
    setSelectedCountry,
    selectedCountry,
  };
};
